import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { leadService } from '../../services/leadService';
import { serviceService } from '../../services/serviceService';


// ─── Metric Card ────────────────────────────────────────────────────────────
const MetricCard = ({ title, value, icon, iconBg, subtitle }) => (
  <div className="bg-card rounded-xl p-5 border border-border flex items-center gap-4">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
      <Icon name={icon} size={22} color="white" />
    </div>
    <div>
      <p className="text-2xl font-bold text-foreground">{value}</p>
      <p className="text-sm font-medium text-foreground">{title}</p>
      {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
    </div>
  </div>
);

// ─── Lead Status Badge ───────────────────────────────────────────────────────
const LeadStatusBadge = ({ status }) => {
  const map = {
    new: 'bg-blue-100 text-blue-700',
    contacted: 'bg-yellow-100 text-yellow-700',
    qualified: 'bg-purple-100 text-purple-700',
    converted: 'bg-green-100 text-green-700',
    closed: 'bg-gray-100 text-gray-600',
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${map?.[status] || 'bg-gray-100 text-gray-600'}`}>
      {status || 'new'}
    </span>
  );
};

// ─── Dashboard Tab ───────────────────────────────────────────────────────────
const DashboardTab = ({ metrics, leads, services }) => {
  const recentLeads = leads?.slice(0, 5) || [];
  return (
    <div className="space-y-6">
      {/* Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {metrics?.map((m, i) => <MetricCard key={i} {...m} />)}
      </div>

      {/* Recent Leads */}
      <div className="bg-card rounded-xl border border-border p-5">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="TrendingUp" size={20} className="text-primary" />
          Recent Leads
        </h3>
        {recentLeads?.length === 0 ? (
          <p className="text-muted-foreground text-sm py-4 text-center">No leads yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 px-3 text-muted-foreground font-medium">Name</th>
                  <th className="text-left py-2 px-3 text-muted-foreground font-medium">Service</th>
                  <th className="text-left py-2 px-3 text-muted-foreground font-medium">Status</th>
                  <th className="text-left py-2 px-3 text-muted-foreground font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {recentLeads?.map((lead, i) => (
                  <tr key={lead?.id || i} className="border-b border-border/50 hover:bg-muted/50 transition-colors">
                    <td className="py-2.5 px-3 font-medium text-foreground">{lead?.name || lead?.fullName || '—'}</td>
                    <td className="py-2.5 px-3 text-muted-foreground">{lead?.serviceRequired || lead?.service || '—'}</td>
                    <td className="py-2.5 px-3"><LeadStatusBadge status={lead?.status} /></td>
                    <td className="py-2.5 px-3 text-muted-foreground text-xs">
                      {lead?.createdAt ? new Date(lead?.createdAt)?.toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Service Summary */}
      <div className="bg-card rounded-xl border border-border p-5">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Package" size={20} className="text-secondary" />
          Service Overview
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-muted rounded-lg">
            <p className="text-2xl font-bold text-foreground">{services?.length || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Total Services</p>
          </div>
          <div className="text-center p-4 bg-muted rounded-lg">
            <p className="text-2xl font-bold text-green-600">{services?.filter(s => s?.status === 'active')?.length || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Active</p>
          </div>
          <div className="text-center p-4 bg-muted rounded-lg">
            <p className="text-2xl font-bold text-yellow-600">{services?.filter(s => s?.isFeatured)?.length || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Featured</p>
          </div>
          <div className="text-center p-4 bg-muted rounded-lg">
            <p className="text-2xl font-bold text-blue-600">{services?.filter(s => s?.status === 'inactive')?.length || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Inactive</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Lead Management Tab ─────────────────────────────────────────────────────
const LeadManagementTab = ({ leads, onStatusUpdate, loading }) => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [updatingId, setUpdatingId] = useState(null);

  const filtered = leads?.filter(lead => {
    const matchSearch = !search ||
      lead?.name?.toLowerCase()?.includes(search?.toLowerCase()) ||
      lead?.fullName?.toLowerCase()?.includes(search?.toLowerCase()) ||
      lead?.email?.toLowerCase()?.includes(search?.toLowerCase()) ||
      lead?.serviceRequired?.toLowerCase()?.includes(search?.toLowerCase());
    const matchStatus = statusFilter === 'all' || lead?.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const handleStatusChange = async (id, newStatus) => {
    setUpdatingId(id);
    await onStatusUpdate(id, newStatus);
    setUpdatingId(null);
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by name, email, service..."
            value={search}
            onChange={e => setSearch(e?.target?.value)}
            className="w-full pl-9 pr-4 py-2.5 border border-border rounded-lg bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e?.target?.value)}
          className="px-4 py-2.5 border border-border rounded-lg bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">All Status</option>
          <option value="new">New</option>
          <option value="contacted">Contacted</option>
          <option value="qualified">Qualified</option>
          <option value="converted">Converted</option>
          <option value="closed">Closed</option>
        </select>
      </div>

      <div className="bg-card rounded-xl border border-border overflow-hidden">
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading leads...</div>
        ) : filtered?.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">No leads found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left py-3 px-4 text-muted-foreground font-medium">Name</th>
                  <th className="text-left py-3 px-4 text-muted-foreground font-medium">Contact</th>
                  <th className="text-left py-3 px-4 text-muted-foreground font-medium">Company</th>
                  <th className="text-left py-3 px-4 text-muted-foreground font-medium">Service Required</th>
                  <th className="text-left py-3 px-4 text-muted-foreground font-medium">Status</th>
                  <th className="text-left py-3 px-4 text-muted-foreground font-medium">Date</th>
                  <th className="text-left py-3 px-4 text-muted-foreground font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered?.map((lead, i) => (
                  <tr key={lead?.id || i} className="border-t border-border hover:bg-muted/40 transition-colors">
                    <td className="py-3 px-4 font-medium text-foreground">{lead?.name || lead?.fullName || '—'}</td>
                    <td className="py-3 px-4">
                      <div className="text-foreground">{lead?.email || '—'}</div>
                      <div className="text-xs text-muted-foreground">{lead?.phone || ''}</div>
                    </td>
                    <td className="py-3 px-4 text-muted-foreground">{lead?.company || '—'}</td>
                    <td className="py-3 px-4 text-muted-foreground">{lead?.serviceRequired || lead?.service || '—'}</td>
                    <td className="py-3 px-4"><LeadStatusBadge status={lead?.status} /></td>
                    <td className="py-3 px-4 text-xs text-muted-foreground">
                      {lead?.createdAt ? new Date(lead?.createdAt)?.toLocaleDateString() : '—'}
                    </td>
                    <td className="py-3 px-4">
                      <select
                        value={lead?.status || 'new'}
                        onChange={e => handleStatusChange(lead?.id, e?.target?.value)}
                        disabled={updatingId === lead?.id}
                        className="text-xs px-2 py-1.5 border border-border rounded bg-card text-foreground focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="qualified">Qualified</option>
                        <option value="converted">Converted</option>
                        <option value="closed">Closed</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground">{filtered?.length} lead{filtered?.length !== 1 ? 's' : ''} found</p>
    </div>
  );
};

// ─── Service Management Tab ──────────────────────────────────────────────────
const ServiceManagementTab = ({ services, onRefresh, loading }) => {
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editService, setEditService] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', status: 'active', isFeatured: false, basePrice: '' });

  const filtered = services?.filter(s =>
    !search || s?.name?.toLowerCase()?.includes(search?.toLowerCase())
  );

  const openAdd = () => {
    setForm({ name: '', description: '', status: 'active', isFeatured: false, basePrice: '' });
    setEditService(null);
    setShowAddModal(true);
  };

  const openEdit = (svc) => {
    setForm({
      name: svc?.name || '',
      description: svc?.description || '',
      status: svc?.status || 'active',
      isFeatured: svc?.isFeatured || false,
      basePrice: svc?.basePrice || '',
    });
    setEditService(svc);
    setShowAddModal(true);
  };

  const handleSave = async () => {
    if (!form?.name?.trim()) return;
    setSaving(true);
    try {
      if (editService) {
        await serviceService?.update(editService?.id, form);
      } else {
        await serviceService?.create(form);
      }
      setShowAddModal(false);
      onRefresh();
    } catch (err) {
      console.error('Save service error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await serviceService?.delete(id);
      setDeleteId(null);
      onRefresh();
    } catch (err) {
      console.error('Delete service error:', err);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="relative flex-1 max-w-sm">
          <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search services..."
            value={search}
            onChange={e => setSearch(e?.target?.value)}
            className="w-full pl-9 pr-4 py-2.5 border border-border rounded-lg bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <Button variant="default" onClick={openAdd}>
          <Icon name="Plus" size={16} className="mr-2" />
          Add Service
        </Button>
      </div>

      <div className="bg-card rounded-xl border border-border overflow-hidden">
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading services...</div>
        ) : filtered?.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">No services found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left py-3 px-4 text-muted-foreground font-medium">Service Name</th>
                  <th className="text-left py-3 px-4 text-muted-foreground font-medium">Description</th>
                  <th className="text-left py-3 px-4 text-muted-foreground font-medium">Base Price</th>
                  <th className="text-left py-3 px-4 text-muted-foreground font-medium">Status</th>
                  <th className="text-left py-3 px-4 text-muted-foreground font-medium">Featured</th>
                  <th className="text-left py-3 px-4 text-muted-foreground font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered?.map((svc, i) => (
                  <tr key={svc?.id || i} className="border-t border-border hover:bg-muted/40 transition-colors">
                    <td className="py-3 px-4 font-medium text-foreground">{svc?.name || '—'}</td>
                    <td className="py-3 px-4 text-muted-foreground max-w-xs">
                      <span className="line-clamp-2">{svc?.description || '—'}</span>
                    </td>
                    <td className="py-3 px-4 text-foreground">{svc?.basePrice ? `₹${svc?.basePrice}` : '—'}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${
                        svc?.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {svc?.status || 'active'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {svc?.isFeatured ? (
                        <Icon name="Star" size={16} className="text-yellow-500 fill-yellow-500" />
                      ) : (
                        <Icon name="Star" size={16} className="text-muted-foreground" />
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEdit(svc)}
                          className="p-1.5 rounded-lg hover:bg-primary/10 text-primary transition-colors"
                          title="Edit"
                        >
                          <Icon name="Pencil" size={15} />
                        </button>
                        <button
                          onClick={() => setDeleteId(svc?.id)}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 transition-colors"
                          title="Delete"
                        >
                          <Icon name="Trash2" size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground">{filtered?.length} service{filtered?.length !== 1 ? 's' : ''} found</p>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-card rounded-2xl shadow-2xl border border-border w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold text-foreground">{editService ? 'Edit Service' : 'Add New Service'}</h3>
              <button onClick={() => setShowAddModal(false)} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
                <Icon name="X" size={20} className="text-muted-foreground" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Service Name *</label>
                <input
                  type="text"
                  value={form?.name}
                  onChange={e => setForm(f => ({ ...f, name: e?.target?.value }))}
                  placeholder="e.g. Business Registration"
                  className="w-full px-3 py-2.5 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Description</label>
                <textarea
                  value={form?.description}
                  onChange={e => setForm(f => ({ ...f, description: e?.target?.value }))}
                  placeholder="Brief description of the service..."
                  rows={3}
                  className="w-full px-3 py-2.5 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Base Price (₹)</label>
                  <input
                    type="number"
                    value={form?.basePrice}
                    onChange={e => setForm(f => ({ ...f, basePrice: e?.target?.value }))}
                    placeholder="0"
                    className="w-full px-3 py-2.5 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Status</label>
                  <select
                    value={form?.status}
                    onChange={e => setForm(f => ({ ...f, status: e?.target?.value }))}
                    className="w-full px-3 py-2.5 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="featured"
                  checked={form?.isFeatured}
                  onChange={e => setForm(f => ({ ...f, isFeatured: e?.target?.checked }))}
                  className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                />
                <label htmlFor="featured" className="text-sm text-foreground">Mark as Featured Service</label>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button variant="outline" className="flex-1" onClick={() => setShowAddModal(false)}>Cancel</Button>
              <Button variant="default" className="flex-1 bg-primary hover:bg-primary/90 text-white border-0" onClick={handleSave} disabled={saving || !form?.name?.trim()}>
                {saving ? 'Saving...' : editService ? 'Update Service' : 'Add Service'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-card rounded-2xl shadow-2xl border border-border w-full max-w-sm p-6 text-center">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Trash2" size={24} className="text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">Delete Service?</h3>
            <p className="text-sm text-muted-foreground mb-6">This action cannot be undone.</p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setDeleteId(null)}>Cancel</Button>
              <Button variant="default" className="flex-1 bg-red-600 hover:bg-red-700 text-white border-0" onClick={() => handleDelete(deleteId)}>
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main Admin Control Panel ────────────────────────────────────────────────
const AdminControlPanel = () => {
  const navigate = useNavigate();
  const { user, signOut, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [leads, setLeads] = useState([]);
  const [services, setServices] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(true);
  const [servicesLoading, setServicesLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/admin-secure-login-portal');
      return;
    }
    loadLeads();
    loadServices();
  }, [user, authLoading]);

  const loadLeads = async () => {
    try {
      setLeadsLoading(true);
      const data = await leadService?.getMyLeads();
      setLeads(data || []);
    } catch (err) {
      console.error('Error loading leads:', err);
    } finally {
      setLeadsLoading(false);
    }
  };

  const loadServices = async () => {
    try {
      setServicesLoading(true);
      const data = await serviceService?.getAllForAdmin();
      setServices(data || []);
    } catch (err) {
      console.error('Error loading services:', err);
    } finally {
      setServicesLoading(false);
    }
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      await leadService?.updateStatus(id, status);
      setLeads(prev => prev?.map(l => l?.id === id ? { ...l, status } : l));
    } catch (err) {
      console.error('Error updating lead status:', err);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/admin-secure-login-portal');
  };

  const metrics = [
    {
      title: 'Total Leads',
      value: leads?.length || 0,
      icon: 'Users',
      iconBg: 'bg-gradient-to-br from-blue-500 to-blue-700',
      subtitle: `${leads?.filter(l => l?.status === 'new' || !l?.status)?.length} new`,
    },
    {
      title: 'Converted Leads',
      value: leads?.filter(l => l?.status === 'converted')?.length || 0,
      icon: 'CheckCircle',
      iconBg: 'bg-gradient-to-br from-green-500 to-green-700',
      subtitle: 'Successfully converted',
    },
    {
      title: 'Total Services',
      value: services?.length || 0,
      icon: 'Package',
      iconBg: 'bg-gradient-to-br from-purple-500 to-purple-700',
      subtitle: `${services?.filter(s => s?.status === 'active')?.length} active`,
    },
    {
      title: 'Featured Services',
      value: services?.filter(s => s?.isFeatured)?.length || 0,
      icon: 'Star',
      iconBg: 'bg-gradient-to-br from-yellow-500 to-orange-500',
      subtitle: 'Highlighted on homepage',
    },
  ];

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: 'LayoutDashboard' },
    { id: 'leads', label: 'Lead Management', icon: 'Users' },
    { id: 'services', label: 'Service Management', icon: 'Package' },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Top Navigation */}
      <header className="bg-card border-b border-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-red-500 to-red-700 rounded-lg flex items-center justify-center">
              <Icon name="Shield" size={18} color="white" />
            </div>
            <div>
              <span className="font-bold text-foreground text-lg">Admin Panel</span>
              <span className="hidden sm:inline text-xs text-muted-foreground ml-2">OBase Group</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-muted-foreground">{user?.email}</span>
            <button
              onClick={handleSignOut}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              <Icon name="LogOut" size={16} />
              <span className="hidden sm:inline">Sign Out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6">
        {/* Tab Navigation */}
        <div className="flex items-center gap-1 mb-6 border-b border-border overflow-x-auto">
          {tabs?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-all duration-200 whitespace-nowrap -mb-px ${
                activeTab === tab?.id
                  ? 'text-primary border-primary' :'text-muted-foreground border-transparent hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <Icon name={tab?.icon} size={17} />
              {tab?.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'dashboard' && (
          <DashboardTab metrics={metrics} leads={leads} services={services} />
        )}
        {activeTab === 'leads' && (
          <div>
            <div className="mb-5">
              <h2 className="text-xl font-semibold text-foreground">Lead Management</h2>
              <p className="text-sm text-muted-foreground mt-1">View and manage all enquiry leads from your website</p>
            </div>
            <LeadManagementTab leads={leads} onStatusUpdate={handleStatusUpdate} loading={leadsLoading} />
          </div>
        )}
        {activeTab === 'services' && (
          <div>
            <div className="mb-5">
              <h2 className="text-xl font-semibold text-foreground">Service Management</h2>
              <p className="text-sm text-muted-foreground mt-1">Add, edit, and manage your service catalog</p>
            </div>
            <ServiceManagementTab services={services} onRefresh={loadServices} loading={servicesLoading} />
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminControlPanel;